import { useQueryClient } from "@tanstack/react-query";
import type { JSX } from "react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useIsAdmin } from "@/shared/auth";

import { useBackfillJob, useLatestBackfillJob } from "../api/use-backfill-job";
import { orderSyncStatusKey, useOrderSyncStatus } from "../api/use-order-sync-status";
import { isJobActive } from "../lib/backfill-job";
import { ageMs, relativeTime } from "../lib/relative-time";

// >1h since the last known update ⇒ warn: the scheduler ticks every 30 min,
// so a stale value usually means it (or the webhook receiver) is down.
const STALE_MS = 60 * 60 * 1000;

/**
 * "Last update 2 min ago" for the Orders header, from the newest
 * marketplace_raw_orders row (GET /v1/orders/sync-status) — advances on a
 * manual Sync, the scheduler's order poll, and inbound marketplace webhooks
 * alike, unlike the old "last backfill job" reading which stayed stale
 * between manual syncs. While a backfill job is active this still shows
 * "Syncing…", polling the same query SyncMenu uses so a page reload keeps
 * showing live state; the backfill job's own detail (per-marketplace counts,
 * history) lives in SyncMenu, not here.
 */
export function OrderSyncFreshness(): JSX.Element | null {
  const { t, i18n } = useTranslation("orders");
  const queryClient = useQueryClient();
  // The backfill list route is admin-only; other roles rely on sync-status alone.
  const isAdmin = useIsAdmin();
  const { data: latestJob, isLoading: isJobListLoading } = useLatestBackfillJob({
    enabled: isAdmin,
  });
  const activeId = latestJob && isJobActive(latestJob.status) ? latestJob.id : null;
  const { data: polledJob } = useBackfillJob(activeId);
  const { data: lastSyncedAt, isLoading: isStatusLoading } = useOrderSyncStatus();

  const job = polledJob ?? latestJob;
  const active = job ? isJobActive(job.status) : false;
  // A backfill job commits its rows the moment it finishes, but sync-status
  // reads a separate resource (marketplace_raw_orders) with its own 30s
  // staleTime — without this, "Last update" would keep showing the pre-sync
  // value until that staleTime happened to expire. Fire the refetch exactly
  // once, on the active→inactive edge, not on every render.
  const wasActiveRef = useRef(active);
  useEffect(() => {
    if (wasActiveRef.current && !active) {
      void queryClient.invalidateQueries({ queryKey: orderSyncStatusKey });
    }
    wasActiveRef.current = active;
  }, [active, queryClient]);

  if (isJobListLoading || isStatusLoading) return null;

  if (active) {
    return (
      <span data-testid="order-sync-freshness" className="text-xs text-[var(--text-subtle)]">
        {t("sync_in_progress")}
      </span>
    );
  }

  if (!lastSyncedAt) {
    return (
      <span data-testid="order-sync-freshness" className="text-xs text-[var(--text-subtle)]">
        {t("sync_never")}
      </span>
    );
  }

  const stale = ageMs(lastSyncedAt) > STALE_MS;
  return (
    <span
      data-testid="order-sync-freshness"
      title={new Date(lastSyncedAt).toLocaleString()}
      className={
        stale
          ? "text-xs font-medium text-[var(--intent-warning-fg,var(--text-primary))]"
          : "text-xs text-[var(--text-subtle)]"
      }
    >
      {stale ? "⚠ " : ""}
      {t("sync_last", { rel: relativeTime(lastSyncedAt, i18n.language) })}
    </span>
  );
}
